import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { BarChart3, RefreshCw, Download, Printer, CheckCircle2, ShieldCheck } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const err = (e) => formatApiError(e?.response?.data?.detail) || "Failed to load report.";
const TYPE_BADGE = { existing: "bg-slate-100 text-slate-600", newly_instrumented: "bg-blue-100 text-blue-700", participant_feedback: "bg-amber-100 text-amber-700" };

function Card({ title, srcKey, sources, children }) {
  const s = sources?.[srcKey];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`beta-card-${srcKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-[#0A0F1C]">{title}</div>
        {s && <span className={`px-2 py-0.5 rounded-full text-[10px] ${TYPE_BADGE[s.type] || "bg-slate-100"}`}>{s.type.replace(/_/g, " ")}</span>}
      </div>
      <div className="text-sm text-slate-600 space-y-0.5">{children}</div>
      {s && <div className="mt-2 text-[10px] text-slate-400">source: {s.source}</div>}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-700">{v ?? "—"}</span></div>
);

export default function BetaReport() {
  const [rep, setRep] = useState(null);
  const [attr, setAttr] = useState(null);
  const [window, setWindow] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/beta/report", { params: { window } }).then((r) => setRep(r.data)).catch((e) => toast.error(err(e))).finally(() => setLoading(false));
    api.get("/admin/leads/scout/attribution").then((r) => setAttr(r.data)).catch(() => setAttr(null));
  }, [window]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/beta/report/export.csv", { params: { window }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `beta_report_${window}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(err(e)); }
  };

  const sg = rep?.system_generated;
  const pf = rep?.participant_feedback;
  const src = rep?.sources;

  return (
    <AppShell title="Controlled Beta Report" subtitle="Read-only · reuses existing sources + beta instrumentation · does not change any operational workflow.">
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="beta-report">
        <Select value={window} onValueChange={setWindow}>
          <SelectTrigger className="w-40 h-9" data-testid="beta-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <button data-testid="beta-refresh" onClick={load} className="h-9 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        <button data-testid="beta-export-csv" onClick={exportCsv} className="h-9 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1.5"><Download size={14} /> Export CSV</button>
        <button data-testid="beta-print" onClick={() => window.print()} className="h-9 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1.5"><Printer size={14} /> Print / PDF</button>
      </div>

      {loading || !rep ? <div className="text-slate-400 text-sm">Loading beta report…</div> : (
        <>
          <div className="mb-3 text-xs text-slate-400">Generated {rep.generated_at?.slice(0, 16)} · window: {rep.window}</div>
          {rep.data_reality && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-xs" data-testid="beta-data-reality">
              <div className="flex items-center gap-1.5 font-semibold text-green-800">
                <ShieldCheck size={14} /> Real participants only — seeded / automated-test activity excluded
              </div>
              <div className="mt-1 text-slate-600">
                {Object.entries(rep.data_reality.by_account_origin || {}).map(([k, v]) => (
                  <span key={k} className="inline-block mr-3">{k.replace(/_/g, " ")}: <b>{v}</b></span>
                ))}
              </div>
              <div className="mt-1 text-slate-400">{rep.data_reality.note}</div>
            </div>
          )}
          <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><BarChart3 size={14} /> System-generated activity <span className="text-green-600">(real)</span></div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            <Card title="Invitations" srcKey="invitations" sources={src}><Row k="Sent" v={sg.invitations.sent} /><Row k="Accepted (registered)" v={sg.invitations.accepted_registered} /><Row k="Accept rate" v={`${sg.invitations.accept_rate_pct}%`} /></Card>
            <Card title="Participants" srcKey="participants" sources={src}><Row k="Registered" v={sg.participants.registered} /><Row k="Active (7d)" v={sg.participants.active_participants_7d} /></Card>
            <Card title="Verification" srcKey="verification" sources={src}><Row k="Verified" v={sg.verification.verified} /><Row k="Pending approval" v={sg.verification.pending_human_approval} /><Row k="Verification rate" v={`${sg.verification.verification_rate_pct}%`} /><Row k="Avg time-to-verify (days)" v={sg.verification.avg_time_to_verify_days} /></Card>
            <Card title="Leads" srcKey="leads" sources={src}><Row k="Created" v={sg.leads.created} /><Row k="Qualified" v={sg.leads.qualified} /></Card>
            <Card title="Deal Rooms" srcKey="deal_rooms" sources={src}><Row k="Created" v={sg.deal_rooms.created} /><Row k="Statuses tracked" v={Object.keys(sg.deal_rooms.by_status || {}).length} /><Row k="Stage transitions" v={Object.keys(sg.deal_rooms.stage_transitions || {}).length} /></Card>
            <Card title="Blockers" srcKey="blockers" sources={src}><Row k="Escrow holds" v={sg.blockers.escrow_holds} /><Row k="Open disputes" v={sg.blockers.open_disputes} /><Row k="Restricted accounts" v={sg.blockers.restricted_accounts} /><Row k="Pending approval" v={sg.blockers.pending_human_approval} /></Card>
            <Card title="Reminders" srcKey="reminders" sources={src}><Row k="Total" v={sg.reminders.total} /><Row k="Ack rate" v={`${sg.reminders.ack_rate_pct}%`} /><Row k="Resolution rate" v={`${sg.reminders.resolution_rate_pct}%`} /></Card>
            <Card title="Security incidents" srcKey="incidents" sources={src}><Row k="Open" v={sg.incidents.open} /><Row k="Critical open" v={sg.incidents.critical_open} /><Row k="Detection alerts" v={sg.incidents.detection_alerts_open} /></Card>
            <Card title="System health" srcKey="system_health" sources={src}><Row k="Capacity label" v={sg.system_health.capacity_label} /><Row k="Scheduler last success" v={(sg.system_health.scheduler_last_success || "—").slice(0, 16)} /></Card>
            <Card title="AI match funnel" srcKey="match_funnel" sources={src}><Row k="Generated" v={sg.match_funnel.generated} /><Row k="Viewed" v={sg.match_funnel.viewed} /><Row k="Saved" v={sg.match_funnel.saved} /><Row k="Dismissed" v={sg.match_funnel.dismissed} /><Row k="Interest" v={sg.match_funnel.interest} /><Row k="Interest rate" v={`${sg.match_funnel.interest_rate_pct}%`} /></Card>
          </div>

          <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><CheckCircle2 size={14} /> Participant-submitted</div>
          <div className="grid gap-3 sm:grid-cols-2 mb-6">
            <Card title="Feedback & satisfaction" srcKey="feedback" sources={src}><Row k="Responses" v={pf.feedback.responses} /><Row k="Avg rating" v={pf.feedback.avg_rating} /><Row k="Promoters (4–5)" v={pf.feedback.promoters_4_5} /><Row k="Detractors (1–2)" v={pf.feedback.detractors_1_2} /></Card>
            <Card title="Support & errors" srcKey="support" sources={src}><Row k="Total" v={pf.support.total} /><Row k="Open" v={pf.support.by_status?.open || 0} /><Row k="Resolved" v={pf.support.by_status?.resolved || 0} /><Row k="Avg time-to-resolution (days)" v={pf.support.avg_time_to_resolution_days} /></Card>
          </div>

          {attr && (Object.keys(attr.by_category || {}).length > 0 || Object.keys(attr.by_counterpart_role || {}).length > 0) && (
            <>
              <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><BarChart3 size={14} /> Recruitment funnel (Scout) — by category & counterpart type <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700">newly instrumented</span></div>
              <div className="grid gap-3 lg:grid-cols-2 mb-6">
                <FunnelTable title="By category" data={attr.by_category} keys={attr.funnel_keys} testid="beta-recruit-by-category" />
                <FunnelTable title="By counterpart type" data={attr.by_counterpart_role} keys={attr.funnel_keys} testid="beta-recruit-by-role" />
              </div>
              <div className="text-[10px] text-slate-400 -mt-4 mb-6">source: leads + invitations (source_type=recruitment_scout) · {attr.note}</div>
            </>
          )}
        </>
      )}
    </AppShell>
  );
}

function FunnelTable({ title, data, keys, testid }) {
  const rows = Object.entries(data || {});
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 overflow-x-auto" data-testid={testid}>
      <div className="text-sm font-semibold text-[#0A0F1C] mb-2">{title}</div>
      {rows.length === 0 ? <p className="text-xs text-slate-400">No recruitment data yet.</p> : (
        <table className="w-full text-[11px]">
          <thead className="text-slate-400"><tr><th className="text-left py-1 pr-2">{title.replace("By ", "")}</th>{keys.map((k) => <th key={k} className="text-right px-1">{k.replace(/_/g, " ")}</th>)}</tr></thead>
          <tbody>{rows.map(([name, f]) => (
            <tr key={name} className="border-t border-slate-100"><td className="py-1 pr-2 font-medium text-slate-600">{name}</td>{keys.map((k) => <td key={k} className="text-right px-1 text-slate-600">{f[k]}</td>)}</tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}
